import React, { useState, useRef, useEffect, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './ModernSelect.css';

/**
 * Tarayıcının standart <select> listesi yerine projenin tasarımına uyan açılır liste.
 * options: [{ value, label, hint?, disabled? }]  ·  onChange(value): seçilen seçeneğin değeri (tipi korunur)
 * hint: listede seçeneğin sağında soluk gösterilen ek bilgi (örn. fiyat)
 * multiple: value bir dizi olur, onChange(dizi) seçenek sırasıyla çağrılır; liste seçimden sonra açık kalır
 * Liste body'ye çizilir: tablo kartı / pencere içinde kesilmez; altta yer yoksa yukarı açılır.
 */
const ModernSelect = ({
  value,
  onChange,
  options,
  ariaLabel,
  placeholder = 'Seçin',
  disabled = false,
  className = '',
  id,
  fullWidth = false,
  multiple = false,
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const typeaheadRef = useRef({ text: '', timer: null });
  const reactId = useId();
  const listId = `ms-list-${reactId}`;

  const selectedValues = multiple ? new Set((Array.isArray(value) ? value : []).map(String)) : null;
  const isOptionSelected = (option) => (multiple ? selectedValues.has(String(option.value)) : String(option.value) === String(value));
  const selectedIndex = options.findIndex(isOptionSelected);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;
  const selectedCount = multiple ? options.filter(isOptionSelected).length : (selected ? 1 : 0);

  const computePosition = useCallback(() => {
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < 240 && spaceAbove > spaceBelow;
    const width = rect.width;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - Math.max(width, 200) - 8));
    return {
      left,
      minWidth: width,
      top: openUp ? undefined : rect.bottom + 6,
      bottom: openUp ? window.innerHeight - rect.top + 6 : undefined,
      maxHeight: Math.max(160, Math.min(320, (openUp ? spaceAbove : spaceBelow) - 16)),
      openUp,
    };
  }, []);

  const firstEnabled = useCallback((from, step) => {
    let i = from;
    for (let n = 0; n < options.length; n++) {
      if (i < 0) i = options.length - 1;
      if (i >= options.length) i = 0;
      if (!options[i].disabled) return i;
      i += step;
    }
    return -1;
  }, [options]);

  const openList = () => {
    if (disabled || !triggerRef.current) return;
    setPosition(computePosition());
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : firstEnabled(0, 1));
    setOpen(true);
  };

  const closeList = (focusTrigger = false) => {
    setOpen(false);
    if (focusTrigger) triggerRef.current?.focus();
  };

  const choose = (index) => {
    const option = options[index];
    if (!option || option.disabled) return;
    if (multiple) {
      // Aç/kapa; liste açık kalır, sonuç seçenek sırasıyla döner
      const nextValues = options
        .filter(o => (o === option ? !isOptionSelected(o) : isOptionSelected(o)))
        .map(o => o.value);
      onChange(nextValues);
      return;
    }
    if (String(option.value) !== String(value)) onChange(option.value);
    closeList(true);
  };

  // Dışarı tıklama, sayfa kaydırma ve yeniden boyutlandırmada kapat
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (triggerRef.current?.contains(e.target) || listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = (e) => {
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer, { passive: true });
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  // Etkin seçeneği görünür tut
  useEffect(() => {
    if (!open || activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-index="${activeIndex}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const handleTypeahead = (char) => {
    const ta = typeaheadRef.current;
    clearTimeout(ta.timer);
    ta.text += char.toLocaleLowerCase('tr-TR');
    ta.timer = setTimeout(() => { ta.text = ''; }, 600);
    const start = open ? activeIndex + 1 : selectedIndex + 1;
    for (let n = 0; n < options.length; n++) {
      const i = (start + n) % options.length;
      const label = String(options[i].label).toLocaleLowerCase('tr-TR');
      if (!options[i].disabled && label.startsWith(ta.text)) {
        if (open) setActiveIndex(i);
        else if (!multiple && String(options[i].value) !== String(value)) onChange(options[i].value);
        return;
      }
    }
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openList();
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) handleTypeahead(e.key);
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(i => firstEnabled(i + 1, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(i => firstEnabled(i - 1, -1));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(firstEnabled(0, 1));
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(firstEnabled(options.length - 1, -1));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        choose(activeIndex);
        break;
      case 'Escape':
        e.preventDefault();
        closeList(true);
        break;
      case 'Tab':
        closeList();
        break;
      default:
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) handleTypeahead(e.key);
    }
  };

  return (
    <>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`ms-trigger${open ? ' is-open' : ''}${fullWidth ? ' is-full' : ''}${selected ? '' : ' is-placeholder'} ${className}`.trim()}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        aria-activedescendant={open && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        disabled={disabled}
        onClick={() => (open ? closeList() : openList())}
        onKeyDown={handleKeyDown}
      >
        <span className="ms-value">{selected ? selected.label : placeholder}</span>
        {multiple && selectedCount > 1 && <span className="ms-count">+{selectedCount - 1}</span>}
        <svg className="ms-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && position && createPortal(
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          aria-multiselectable={multiple || undefined}
          className={`ms-list${position.openUp ? ' is-up' : ''}${multiple ? ' is-multiple' : ''}`}
          style={{
            left: position.left,
            minWidth: position.minWidth,
            top: position.top,
            bottom: position.bottom,
            maxHeight: position.maxHeight,
          }}
        >
          {options.map((option, index) => {
            const isSelected = multiple ? isOptionSelected(option) : index === selectedIndex;
            return (
              <li
                key={`${option.value}`}
                id={`${listId}-${index}`}
                data-index={index}
                role="option"
                aria-selected={isSelected}
                aria-disabled={option.disabled || undefined}
                className={`ms-option${isSelected ? ' is-selected' : ''}${index === activeIndex ? ' is-active' : ''}${option.disabled ? ' is-disabled' : ''}`}
                onMouseEnter={() => !option.disabled && setActiveIndex(index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(index)}
              >
                {multiple && (
                  <span className="ms-box" aria-hidden="true">
                    {isSelected && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                  </span>
                )}
                <span className="ms-option-label">{option.label}</span>
                {option.hint && <span className="ms-option-hint">{option.hint}</span>}
                {!multiple && isSelected && (
                  <svg className="ms-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>,
        document.body
      )}
    </>
  );
};

export default ModernSelect;
